// Tile types. A tile is no longer just "solid" or "empty" — it's a
// small STACK: a base MATERIAL (rock, wood, ...) with an optional
// MODIFIER on top (a door, ...). "wood base + door modifier" is a
// wooden door. The modifier, when present, overrides the base's
// solidity and look, so a door in a wall is a passable gap you walk
// through.
//
// A tile is packed into one byte so a chunk's grid stays a
// Uint8Array: the base id in the low nibble, the modifier id in the
// high nibble. 16 materials × 16 modifiers is plenty, and it keeps
// the whole terrain layer (generation, overlay, sync) dealing in
// plain small integers.
//
// Air is id 0 / packed 0 — an empty tile is the number zero, so the
// grid still zero-fills to "nothing", and `setSolid(false)` still
// means packed 0.

import TILES from "../../data/tiles.json" with { type: "json" };

// id -> definition, for O(1) lookup on the hot path.
const MAT = [];
for (const [name, m] of Object.entries(TILES.materials)) MAT[m.id] = { name, ...m };
const MOD = [];
for (const [name, m] of Object.entries(TILES.modifiers)) MOD[m.id] = { name, ...m };

const MAT_ID = Object.fromEntries(MAT.map((m) => [m.name, m.id]));
const MOD_ID = Object.fromEntries(MOD.map((m) => [m.name, m.id]));

export const AIR = 0;

export function pack(baseId, modId = 0) { return (baseId & 0x0f) | ((modId & 0x0f) << 4); }

// The biomes, in order — each names the base material its terrain is built from, and
// carries that as a ready-packed tile. World generation (engine/chunks.js) picks one
// per region from a low-frequency noise field, so roaming crosses rock highlands into
// sand desert, mossy grove, ice tundra, and so on. Index 0 is the default biome (the
// spawn area is pinned to it), and BIOME_BASE is its base — what "just solid" means
// when no biome is in play (e.g. filling a tile in).
export const BIOMES = TILES.biomes.map((b) => ({ ...b, baseTile: pack(MAT_ID[b.base], 0) }));
export const BIOME_NOISE = TILES.biomeNoise;
export const BIOME_BASE = BIOMES[0].baseTile;
export const baseOf = (tile) => tile & 0x0f;
export const modOf = (tile) => (tile >> 4) & 0x0f;

// Put a named modifier on a tile, keeping its base material: withModifier(rockTile,
// "doorOpen") -> a passable rock doorway. An unknown name leaves the tile unchanged.
export const withModifier = (tile, name) => pack(baseOf(tile), MOD_ID[name] ?? modOf(tile));

// Doors. A door is either state of the same opening — open (passable) or closed
// (solid) — carried as a modifier on whatever material it's cut into, so a door reads
// in the colour of its wall. Toggling swaps the two states, keeping the base.
export const isDoor = (tile) => { const m = modOf(tile); return m === MOD_ID.doorOpen || m === MOD_ID.doorClosed; };
export const isDoorOpen = (tile) => modOf(tile) === MOD_ID.doorOpen;
export const toggleDoor = (tile) => withModifier(tile, isDoorOpen(tile) ? "doorClosed" : "doorOpen");

// Solidity: the modifier wins if it has an opinion (a door is
// passable on any base), otherwise the base material decides.
export function isSolidTile(tile) {
    const mod = MOD[modOf(tile)];
    if (mod && mod.id !== 0 && "solid" in mod) return mod.solid;
    const mat = MAT[baseOf(tile)];
    return !!(mat && mat.solid);
}

// Colours: the modifier's if it declares one, else the base material's. Doors declare
// none, so a door takes the colour of the wall it's cut into — the renderer draws its
// door overlay from that base colour rather than a fixed door tint.
export const fillOf = (tile) => { const m = MOD[modOf(tile)]; return (m && m.fill) || MAT[baseOf(tile)]?.fill || "#33405e"; };
export const lineOf = (tile) => { const m = MOD[modOf(tile)]; return (m && m.line) || MAT[baseOf(tile)]?.line || "#5f7bb0"; };

// A structure/mapping token -> what it places. "tile:wood mod:door"
// -> a packed tile; "loot:armor" -> a loot marker; a bare
// material/modifier name works too. Returns { tile } or { loot } or
// null (unknown).
export function parseToken(token) {
    let base = null, mod = 0, loot = null;
    for (const part of token.trim().split(/\s+/)) {
        const i = part.indexOf(":");
        const kind = i < 0 ? "" : part.slice(0, i);
        const value = i < 0 ? part : part.slice(i + 1);
        if (kind === "loot") loot = value;                       // loot:armor
        else if (kind === "mod") mod = MOD_ID[value] ?? mod;     // mod:door
        else if (kind === "tile") base = MAT_ID[value] ?? base;  // tile:wood
        else if (value in MAT_ID) base = MAT_ID[value];          // bare material name
        else if (value in MOD_ID) mod = MOD_ID[value];           // bare modifier name
    }
    if (loot) return { loot };
    if (base != null || mod) return { tile: pack(base ?? MAT_ID[BIOMES[0].base], mod) };
    return null;
}

export const materialNames = () => MAT.filter(Boolean).map((m) => m.name);
export const modifierNames = () => MOD.filter(Boolean).map((m) => m.name);

// The solid materials a player can carry and build with — each with its packed tile and
// its colours, for the inventory panel and the trowel's block wheel. Air and door
// modifiers aren't buildable stock, so they're not here — nor is a material flagged
// `buildable:false` (the `custom` placeholder that block-maker tiles wear, Set 19: it's a
// carrier for a per-tile pixel design, not a plain material you'd pick off a wheel).
export const SOLID_MATERIALS = MAT.filter((m) => m && m.solid && m.buildable !== false).map((m) => ({ name: m.name, tile: pack(m.id, 0), fill: m.fill, line: m.line }));
export const CUSTOM_TILE = MAT_ID.custom != null ? pack(MAT_ID.custom, 0) : AIR; // the packed tile a custom block wears
export const isMaterial = (name) => SOLID_MATERIALS.some((m) => m.name === name);
export const materialTile = (name) => (MAT_ID[name] != null ? pack(MAT_ID[name], 0) : AIR);
export const materialOf = (tile) => (MAT[baseOf(tile)] || {}).name || null; // what a packed tile is made of

export { TILES };
